import { useRef, useEffect, useState } from 'preact/hooks';
import { isValidElement, cloneElement } from 'preact/compat';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

const PIN_URL = window.BARREL.serviceCenter.images.pinUrl;
const MARKER_URL = window.BARREL.serviceCenter.images.markerUrl;
const MARKER_SELECTED_URL = window.BARREL.serviceCenter.images.markerSelectedUrl;

const Map = ({ vendors, selectedVendor, mapLoaded, locationChanged, currentLocation, apiKey }) => {
    const mapStyle = 'height: calc(100vh - 70px);';

    const defaultZoom = 4;

    const [center, setCenter] = useState(currentLocation);
    const [zoom, setZoom] = useState(defaultZoom);
    const [markers, setMarkers] = useState([]);

    const getMarkerFromVendor = (vendor) => {
        if (!vendor) {
            return null;
        }

        const coordinates = vendor.coordinates;

        return {
            vendorName: vendor.vendorName,
            position: {
                lat: coordinates.latitude,
                lng: coordinates.longitude
            },
            info: getMarkerInfo(vendor),
            showInfoWindow: vendor.showInfoWindow,
            selected: vendor.selected
        }
    }

    useEffect(() => {
        if (currentLocation) {
            setCenter(currentLocation);
        }
    }, [currentLocation]);

    const getMarkersLocations = (list) => {
        const lst = list.filter(item => item.currentLocation !== true);

        const latArr = lst.map(item => item.position.lat).sort();
        const lngArr = lst.map(item => item.position.lng).sort();

        return {
            latArr,
            lngArr
        }
    }

    const getZoomByBound = (bound) => {
        let zoom = defaultZoom;

        if (bound > 0 && bound < 0.05) {
            zoom = 14;
        }
        else if (bound >= 0.05 && bound < 0.1) {
            zoom = 13;
        }
        else if (bound >= 0.1 && bound < 0.22) {
            zoom = 12;
        }
        else if (bound >= 0.22 && bound < 0.4) {
            zoom = 11;
        }
        else if (bound >= 0.4 && bound < 0.9) {
            zoom = 10;
        }
        else if (bound >= 0.9 && bound < 1.5) {
            zoom = 9;
        }
        else if (bound >= 1.5 && bound < 1.9) {
            zoom = 8;
        }
        else if (bound >= 1.9 && bound < 4) {
            zoom = 8;
        }
        else if (bound >= 4 && bound < 7.5) {
            zoom = 7;
        }
        else if (bound >= 7.5 && bound < 14) {
            zoom = 6;
        }
        else if (bound >= 14 && bound < 16) {
            zoom = 5;
        }

        return zoom;
    }

    const getMarkersLocationData = (list) => {
        const { latArr, lngArr } = getMarkersLocations(list);

        if (latArr && latArr.length > 0 && lngArr && lngArr.length > 0) {
            const latMin = latArr[0];
            const latMax = latArr[latArr.length - 1];
            const latBound = latMax - latMin;
            const latMiddle = (latMax + latMin) / 2;

            const lngMin = lngArr[0];
            const lngMax = lngArr[lngArr.length - 1];
            const lngBound = lngMax - lngMin;
            const lngMiddle = (lngMax + lngMin) / 2;

            const maxBound = Math.max(Math.abs(latBound), Math.abs(lngBound));
            const zoom = getZoomByBound(maxBound);

            return {
                zoom,
                center: {
                    lat: latMiddle,
                    lng: lngMiddle
                }
            };
        }

        return {
            zoom: defaultZoom,
            center: currentLocation
        };

    }

    useEffect(() => {
        const list = [];

        vendors.map(item => {
            item.selected = false;
            const marker = getMarkerFromVendor(item);

            list.push(marker);
        });

        if (currentLocation) {
            list.push({
                currentLocation: true,
                position: currentLocation
            });
        }

        setMarkers(list);

        const { zoom, center } = getMarkersLocationData(list);

        if (zoom) {
            setZoom(zoom);
        }

        if (center) {
            setCenter(center);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendors]);

    useEffect(() => {
        if (selectedVendor) {
            const coordinates = selectedVendor.coordinates;

            setCenter({
                lat: coordinates.latitude,
                lng: coordinates.longitude
            });

            setZoom(12);

            const arr = markers.filter(item => (item.position.lat !== coordinates.latitude &&
                item.position.lng !== coordinates.longitude));

            arr.map(item => {
                item.selected = false;
                item.showInfoWindow = false;
            });

            selectedVendor.selected = true;
            selectedVendor.showInfoWindow = true;
            const marker = getMarkerFromVendor(selectedVendor);

            arr.push(marker);

            setMarkers(arr);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVendor]);

    const getMarkerInfo = (vendor) => {
        let info = `<div><strong>${vendor.vendorName}</strong></div><div style="font-size:1px">&nbsp;</div>`;
        info += `<div>${vendor.address1}</div>`;

        if (vendor.address2 && vendor.address2.length > 0) {
            info += `<div>${vendor.address2}</div>`;
        }

        info += `<div>${vendor.city}, ${vendor.state} ${vendor.postalCode}</div>`;

        if (vendor.phone && vendor.phone.length > 0) {
            info += `<div><strong>${vendor.phone}</strong></div>`;
        }

        return info;
    }

    const render = (status) => {
        if (status === Status.FAILURE) return <div>ErrorComponent</div>;
        return 'Loading Map...';
    };

    useEffect(() => {
        if (locationChanged) {
            const lat = locationChanged.lat;
            const lng = locationChanged.lng;

            setCenter({
                lat,
                lng
            });

            setZoom(12);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationChanged]);

    const Map = ({ center, zoom, children, style, ...options }) => {
        const [map, setMap] = useState();
        const ref = useRef();

        useEffect(() => {
            if (ref.current && !map) {
                setMap(new window.google.maps.Map(ref.current, { center, zoom }));
                mapLoaded(true);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [ref, map]);

        return (
            <>
                <div ref={ref} style={style} />
                {children.map((child) => {
                    if (isValidElement(child)) {
                        return cloneElement(child, { map });
                    }
                })}
            </>
        );
    }

    const Marker = (options) => {
        const [marker, setMarker] = useState();

        useEffect(() => {
            if (!marker) {
                if (options.currentLocation) {
                    setMarker(new window.google.maps.Marker({
                        icon: PIN_URL
                    }));
                }
                else if (options.selected) {
                    setMarker(new window.google.maps.Marker({
                        icon: MARKER_SELECTED_URL
                    }));
                }
                else {
                    setMarker(new window.google.maps.Marker({
                        icon: MARKER_URL
                    }));
                }
            }

            return () => {
                if (marker) {
                    marker.setMap(null);
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [marker]);

        useEffect(() => {
            if (marker) {
                marker.setOptions(options);

                if (marker.showInfoWindow === true) {
                    const infoWindow = getInfoWindow(marker);

                    infoWindow.open({
                        anchor: marker,
                        shouldFocus: false,
                    });
                }

                marker.addListener('click', () => {
                    const infowindow = new window.google.maps.InfoWindow({
                        content: marker.info,
                    });

                    infowindow.open({
                        anchor: marker,
                        shouldFocus: false,
                    });
                });
            }
        }, [marker, options]);

        return null;
    };

    const getInfoWindow = (marker) => {
        const infoWindow = new window.google.maps.InfoWindow({
            content: marker.info,
        });

        return infoWindow;
    }

    return (
        <Wrapper apiKey={apiKey} libraries={['places']} render={render}>
            <Map class='w-full' center={center} zoom={zoom} style={mapStyle}>
                {markers.map((marker, index) => (
                    <Marker
                        key={index}
                        position={marker.position}
                        info={marker.info}
                        showInfoWindow={marker.showInfoWindow}
                        currentLocation={marker.currentLocation}
                        selected={marker.selected}
                    />
                ))}
            </Map>
        </Wrapper>
    )
}

export default Map;